import { useState, useEffect } from 'react';
import axios from 'axios';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import {
  ChevronUp, ChevronDown, Plus, Trash2, X, GripVertical, Save, Copy
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Page types that are STRUCTURALLY REQUIRED for the onboarding flow to function.
// Removing or disabling these would break account creation, so they're locked
// from delete / disable / duplicate (text content stays editable).
const CRITICAL_PAGE_TYPES = ['email_capture', 'password_capture', 'payment_capture'];
const ALWAYS_ON_PAGE_TYPES = ['email_capture', 'password_capture']; // can't toggle enabled/required
const isCriticalPage = (page) => page && CRITICAL_PAGE_TYPES.includes(page.type);
const isAlwaysOnPage = (page) => page && ALWAYS_ON_PAGE_TYPES.includes(page.type);

// Render-prop wrapper that applies @dnd-kit's sortable transforms to a page
// row. The drag handle (GripVertical) inside the row receives `attributes` +
// `listeners` so that ONLY the grip drags — clicks elsewhere stay normal.
function SortablePageWrapper({ page, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: page.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ attributes, listeners })}
    </div>
  );
}

const PAGE_TYPES = [
  { value: 'intro', label: 'Intro (informational page)' },
  { value: 'features', label: 'Features (bulleted list with tick icons)' },
  { value: 'counter', label: 'Counter (number picker)' },
  { value: 'multi_select', label: 'Multi Select (option buttons)' },
  { value: 'single_select', label: 'Single Select (one choice — e.g. Yes/No)' },
  { value: 'text_input', label: 'Text Input' },
  { value: 'file_upload', label: 'File Upload' },
  { value: 'color_picker', label: 'Colour Picker' },
  { value: 'email_capture', label: 'Email Capture (email + Google login) — required for sign-up' },
  { value: 'password_capture', label: 'Password Capture (password + confirm) — required for sign-up' },
  { value: 'payment_capture', label: 'Payment Capture (embedded Stripe checkout) — replaces hosted redirect' },
];

const ICON_OPTIONS = ['football', 'timer', 'award', 'users', 'shield', 'user', 'palette', 'star', 'heart', 'flag', 'target'];
const ICON_BG_OPTIONS = ['emerald', 'blue', 'purple', 'amber', 'red', 'pink', 'indigo', 'teal', 'gradient'];

export function OnboardingTab({ saveRef }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedPage, setExpandedPage] = useState(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/onboarding-config`);
      setConfig(res.data);
    } catch {
      toast.error('Failed to load onboarding config');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/onboarding-config`, {
        pages: config.pages,
        plan_page: config.plan_page,
        success_page: config.success_page,
        welcome_email: config.welcome_email,
        step_labels: config.step_labels,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Onboarding config saved');
    } catch {
      toast.error('Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  // Raw save function (no toast) for parent global Save button
  const saveConfigSilent = async () => {
    if (!config) return;
    const token = localStorage.getItem('token');
    await axios.put(`${API}/onboarding-config`, {
      pages: config.pages,
      plan_page: config.plan_page,
      success_page: config.success_page,
      welcome_email: config.welcome_email,
      step_labels: config.step_labels,
    }, { headers: { Authorization: `Bearer ${token}` } });
  };

  // Register save function on parent ref
  useEffect(() => {
    if (saveRef) saveRef.current = saveConfigSilent;
    return () => { if (saveRef) saveRef.current = null; };
  });

  // DnD sensors — pointer + keyboard for accessibility.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setConfig(prev => {
      const pages = prev.pages || [];
      const oldIndex = pages.findIndex(p => p.id === active.id);
      const newIndex = pages.findIndex(p => p.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return { ...prev, pages: arrayMove(pages, oldIndex, newIndex) };
    });
  };

  const updatePage = (index, field, value) => {
    setConfig(prev => {
      const pages = [...prev.pages];
      const target = pages[index];
      // Lock immutable fields on critical pages so admins can't break sign-up.
      if (isCriticalPage(target) && ['type', 'field_key'].includes(field)) {
        toast.error(`This field is locked on the ${target.type} page — it's required for sign-up.`);
        return prev;
      }
      // Email + password pages are also "always on" — can't be disabled or made optional.
      if (isAlwaysOnPage(target) && ['enabled', 'required'].includes(field)) {
        toast.error(`This field is locked on the ${target.type} page — it's required for sign-up.`);
        return prev;
      }
      pages[index] = { ...pages[index], [field]: value };
      return { ...prev, pages };
    });
  };

  const movePage = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= config.pages.length) return;
    setConfig(prev => {
      const pages = [...prev.pages];
      [pages[index], pages[newIndex]] = [pages[newIndex], pages[index]];
      return { ...prev, pages };
    });
    setExpandedPage(newIndex);
  };

  const addPage = () => {
    const newPage = {
      id: `custom_${Date.now()}`,
      type: 'text_input',
      title: 'New Page',
      subtitle: 'Enter a description',
      icon: 'star',
      icon_bg: 'emerald',
      field_key: `custom_${Date.now()}`,
      enabled: true,
      required: false,
      placeholder: 'Enter text...',
      counter_label: '',
      counter_min: 0,
      counter_max: 10,
      counter_default: 1,
      options: [],
    };
    setConfig(prev => ({
      ...prev,
      pages: [...prev.pages, newPage],
    }));
    setExpandedPage(config.pages.length);
  };

  const removePage = (index) => {
    const target = config.pages[index];
    if (isCriticalPage(target)) {
      toast.error(`This page is required for sign-up and can't be removed (${target.type}).`);
      return;
    }
    setConfig(prev => ({
      ...prev,
      pages: prev.pages.filter((_, i) => i !== index),
    }));
    setExpandedPage(null);
  };

  const duplicatePage = (index) => {
    const target = config.pages[index];
    if (isCriticalPage(target)) {
      toast.error(`This page is required for sign-up and can't be duplicated (${target.type}).`);
      return;
    }
    setConfig(prev => {
      const original = prev.pages[index];
      // Deep clone via JSON to break references on nested arrays/objects (options, etc.)
      const clone = JSON.parse(JSON.stringify(original));
      // Generate a fresh id and a fresh field_key so the duplicate doesn't share state
      clone.id = `page_${Date.now()}`;
      clone.field_key = `custom_${Date.now()}`;
      if (clone.title) clone.title = `${clone.title} (Copy)`;
      const pages = [...prev.pages];
      pages.splice(index + 1, 0, clone);
      return { ...prev, pages };
    });
    // Open the new copy for editing
    setExpandedPage(index + 1);
    toast.success('Page duplicated');
  };

  const addOption = (pageIndex) => {
    setConfig(prev => {
      const pages = [...prev.pages];
      pages[pageIndex] = {
        ...pages[pageIndex],
        options: [...(pages[pageIndex].options || []), 'New Option'],
      };
      return { ...prev, pages };
    });
  };

  const updateOption = (pageIndex, optIndex, value) => {
    setConfig(prev => {
      const pages = [...prev.pages];
      const options = [...pages[pageIndex].options];
      options[optIndex] = value;
      pages[pageIndex] = { ...pages[pageIndex], options };
      return { ...prev, pages };
    });
  };

  const removeOption = (pageIndex, optIndex) => {
    setConfig(prev => {
      const pages = [...prev.pages];
      pages[pageIndex] = {
        ...pages[pageIndex],
        options: pages[pageIndex].options.filter((_, i) => i !== optIndex),
      };
      return { ...prev, pages };
    });
  };

  const moveOption = (pageIndex, optIndex, direction) => {
    setConfig(prev => {
      const pages = [...prev.pages];
      const opts = [...(pages[pageIndex].options || [])];
      const target = optIndex + direction;
      if (target < 0 || target >= opts.length) return prev;
      [opts[optIndex], opts[target]] = [opts[target], opts[optIndex]];
      pages[pageIndex] = { ...pages[pageIndex], options: opts };
      return { ...prev, pages };
    });
  };

  const addBullet = (pageIndex) => {
    setConfig(prev => {
      const pages = [...prev.pages];
      pages[pageIndex] = {
        ...pages[pageIndex],
        bullets: [...(pages[pageIndex].bullets || []), 'New bullet point'],
      };
      return { ...prev, pages };
    });
  };

  const updateBullet = (pageIndex, bIndex, value) => {
    setConfig(prev => {
      const pages = [...prev.pages];
      const bullets = [...(pages[pageIndex].bullets || [])];
      bullets[bIndex] = value;
      pages[pageIndex] = { ...pages[pageIndex], bullets };
      return { ...prev, pages };
    });
  };

  const removeBullet = (pageIndex, bIndex) => {
    setConfig(prev => {
      const pages = [...prev.pages];
      pages[pageIndex] = {
        ...pages[pageIndex],
        bullets: (pages[pageIndex].bullets || []).filter((_, i) => i !== bIndex),
      };
      return { ...prev, pages };
    });
  };

  const moveBullet = (pageIndex, bIndex, direction) => {
    setConfig(prev => {
      const pages = [...prev.pages];
      const bullets = [...(pages[pageIndex].bullets || [])];
      const target = bIndex + direction;
      if (target < 0 || target >= bullets.length) return prev;
      [bullets[bIndex], bullets[target]] = [bullets[target], bullets[bIndex]];
      pages[pageIndex] = { ...pages[pageIndex], bullets };
      return { ...prev, pages };
    });
  };

  const updatePlanPage = (field, value) => {
    setConfig(prev => ({
      ...prev,
      plan_page: { ...prev.plan_page, [field]: value },
    }));
  };

  const updateSuccessPage = (field, value) => {
    setConfig(prev => ({
      ...prev,
      success_page: { ...(prev.success_page || {}), [field]: value },
    }));
  };

  const updateWelcomeEmail = (field, value) => {
    setConfig(prev => ({
      ...prev,
      welcome_email: { ...(prev.welcome_email || {}), [field]: value },
    }));
  };

  const updateWelcomeBullet = (index, value) => {
    setConfig(prev => {
      const bullets = [...((prev.welcome_email || {}).whats_next_bullets || [])];
      bullets[index] = value;
      return { ...prev, welcome_email: { ...(prev.welcome_email || {}), whats_next_bullets: bullets } };
    });
  };

  const addWelcomeBullet = () => {
    setConfig(prev => {
      const bullets = [...((prev.welcome_email || {}).whats_next_bullets || []), 'New step'];
      return { ...prev, welcome_email: { ...(prev.welcome_email || {}), whats_next_bullets: bullets } };
    });
  };

  const removeWelcomeBullet = (index) => {
    setConfig(prev => {
      const bullets = [...((prev.welcome_email || {}).whats_next_bullets || [])];
      bullets.splice(index, 1);
      return { ...prev, welcome_email: { ...(prev.welcome_email || {}), whats_next_bullets: bullets } };
    });
  };

  const moveWelcomeBullet = (index, direction) => {
    setConfig(prev => {
      const bullets = [...((prev.welcome_email || {}).whats_next_bullets || [])];
      const target = index + direction;
      if (target < 0 || target >= bullets.length) return prev;
      [bullets[index], bullets[target]] = [bullets[target], bullets[index]];
      return { ...prev, welcome_email: { ...(prev.welcome_email || {}), whats_next_bullets: bullets } };
    });
  };

  if (loading) return <p className="text-sm text-gray-500">Loading onboarding config...</p>;
  if (!config) return <p className="text-sm text-red-500">Failed to load config.</p>;

  return (
    <div className="space-y-6" data-testid="onboarding-tab">
      {/* Global onboarding settings */}
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 flex items-center justify-between" data-testid="onboarding-settings">
        <div>
          <h4 className="text-sm font-semibold text-gray-700">Page transition animations</h4>
          <p className="text-xs text-gray-500 mt-0.5">Logo spin + page slide-in when moving between onboarding pages.</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={config.animations_enabled !== false}
            onChange={(e) => setConfig(prev => ({ ...prev, animations_enabled: e.target.checked }))}
            className="sr-only peer"
            data-testid="onboarding-animations-toggle"
          />
          <div className="w-11 h-6 bg-gray-200 peer-checked:bg-emerald-600 rounded-full peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
        </label>
      </div>

      {/* Onboarding Pages */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Onboarding Pages</h3>
          <Button onClick={addPage} size="sm" className="gap-1.5" data-testid="add-page-btn">
            <Plus className="w-4 h-4" /> Add Page
          </Button>
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={config.pages.map(p => p.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {config.pages.map((page, index) => (
                <SortablePageWrapper page={page} key={page.id}>
                  {({ attributes, listeners }) => (
                    <Card className={`border ${!page.enabled ? 'opacity-60' : ''}`}>
                      <div
                        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => setExpandedPage(expandedPage === index ? null : index)}
                        data-testid={`page-header-${index}`}
                      >
                        <button
                          type="button"
                          {...attributes}
                          {...listeners}
                          onClick={(e) => e.stopPropagation()}
                          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-700 touch-none flex-shrink-0"
                          title="Drag to reorder"
                          data-testid={`page-grip-${index}`}
                        >
                          <GripVertical className="w-4 h-4" />
                        </button>
                        <span className="text-xs font-medium text-gray-400 w-6">{index + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{page.title}</p>
                  <p className="text-xs text-gray-500">{page.type} {page.required ? '(required)' : '(optional)'}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Switch
                    checked={page.enabled}
                    onCheckedChange={(checked) => { updatePage(index, 'enabled', checked); }}
                    onClick={(e) => e.stopPropagation()}
                    disabled={isAlwaysOnPage(page)}
                    title={isAlwaysOnPage(page) ? 'Required for sign-up — can\'t be disabled' : undefined}
                    data-testid={`page-toggle-${index}`}
                  />
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); movePage(index, -1); }} disabled={index === 0} className="h-7 w-7 p-0">
                    <ChevronUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); movePage(index, 1); }} disabled={index === config.pages.length - 1} className="h-7 w-7 p-0">
                    <ChevronDown className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); duplicatePage(index); }}
                    disabled={isCriticalPage(page)}
                    className="h-7 w-7 p-0 text-gray-500 hover:text-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed"
                    title={isCriticalPage(page) ? 'Required for sign-up — can\'t be duplicated' : 'Duplicate page'}
                    data-testid={`duplicate-page-${index}`}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

                      {expandedPage === index && (
                        <CardContent className="border-t pt-4 space-y-4">
                          {isCriticalPage(page) && (
                    <div
                      className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-xs px-3 py-2 flex items-start gap-2"
                      data-testid={`critical-page-banner-${index}`}
                    >
                      <span className="font-semibold">Locked page —</span>
                      <span>
                        {page.type === 'email_capture'
                          ? "This page collects the coach's email for sign-up. You can edit text & placeholders but the page type, field key, enabled toggle, and required flag are locked."
                          : page.type === 'password_capture'
                          ? "This page collects the coach's password for sign-up. You can edit text & placeholders but the page type, field key, enabled toggle, and required flag are locked."
                          : "Embedded Stripe checkout. You can enable/disable this page (A/B-test against the hosted redirect) and edit copy, but page type and field key are locked."}
                      </span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Page Title</Label>
                      <Input value={page.title} onChange={e => updatePage(index, 'title', e.target.value)} className="mt-1" data-testid={`page-title-${index}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Page Type {isCriticalPage(page) && <span className="text-amber-600">(locked)</span>}</Label>
                      <select
                        value={page.type}
                        onChange={e => updatePage(index, 'type', e.target.value)}
                        disabled={isCriticalPage(page)}
                        className={`mt-1 w-full h-9 rounded-md border border-gray-200 px-3 text-sm ${isCriticalPage(page) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                        data-testid={`page-type-${index}`}
                      >
                        {PAGE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Subtitle / Description</Label>
                    <Input value={page.subtitle} onChange={e => updatePage(index, 'subtitle', e.target.value)} className="mt-1" data-testid={`page-subtitle-${index}`} />
                  </div>

                  {/* Body Text — relevant for intro + single_select pages. Lives right under
                     Subtitle so content authors see the full copy stack in reading order. */}
                  {(page.type === 'intro' || page.type === 'single_select') && (
                    <div>
                      <Label className="text-xs">Body Text <span className="text-gray-400">(optional — leave blank to hide on the page)</span></Label>
                      <textarea
                        value={page.body_text || ''}
                        onChange={e => updatePage(index, 'body_text', e.target.value)}
                        rows={4}
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                        placeholder="Longer description shown beneath the subtitle. Supports line breaks."
                        data-testid={`page-body-${index}`}
                      />
                    </div>
                  )}

                  <div>
                    <Label className="text-xs">Next Button Text <span className="text-gray-400">(defaults to "Next")</span></Label>
                    <Input value={page.next_button_text || ''} onChange={e => updatePage(index, 'next_button_text', e.target.value)} className="mt-1" placeholder="Next" data-testid={`page-next-button-text-${index}`} />
                  </div>

                  <div>
                    <Label className="text-xs">Field Key <span className="text-gray-400">(advanced — must be unique across pages{isCriticalPage(page) ? '; locked on this page' : ''})</span></Label>
                    <Input
                      value={page.field_key || ''}
                      onChange={e => updatePage(index, 'field_key', e.target.value)}
                      disabled={isCriticalPage(page)}
                      className={`mt-1 font-mono text-xs ${isCriticalPage(page) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                      placeholder="e.g. coaching_badges"
                      data-testid={`page-field-key-${index}`}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs">Icon</Label>
                      <select value={page.icon || ''} onChange={e => updatePage(index, 'icon', e.target.value)} className="mt-1 w-full h-9 rounded-md border border-gray-200 px-3 text-sm" data-testid={`page-icon-select-${index}`}>
                        <option value="">— None —</option>
                        {ICON_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Icon Colour</Label>
                      <select value={page.icon_bg} onChange={e => updatePage(index, 'icon_bg', e.target.value)} className="mt-1 w-full h-9 rounded-md border border-gray-200 px-3 text-sm">
                        {ICON_BG_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="flex items-end gap-3 col-span-1 sm:col-span-2">
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={page.required}
                          onChange={e => updatePage(index, 'required', e.target.checked)}
                          disabled={isAlwaysOnPage(page)}
                          title={isAlwaysOnPage(page) ? 'Required for sign-up — locked' : undefined}
                        />
                        Required{isAlwaysOnPage(page) && <span className="text-amber-600"> (locked)</span>}
                      </label>
                      <label className="flex items-center gap-2 text-xs">
                        <input type="checkbox" checked={!!page.keep_card_bg_mobile} onChange={e => updatePage(index, 'keep_card_bg_mobile', e.target.checked)} data-testid={`page-keep-card-bg-${index}`} />
                        Show card background on mobile
                      </label>
                    </div>
                  </div>

                  {/* Counter-specific fields */}
                  {page.type === 'counter' && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs">Label</Label>
                        <Input value={page.counter_label || ''} onChange={e => updatePage(index, 'counter_label', e.target.value)} className="mt-1" placeholder="e.g. years" />
                      </div>
                      <div>
                        <Label className="text-xs">Min</Label>
                        <Input type="number" value={page.counter_min || 0} onChange={e => updatePage(index, 'counter_min', parseInt(e.target.value) || 0)} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">Max</Label>
                        <Input type="number" value={page.counter_max || 10} onChange={e => updatePage(index, 'counter_max', parseInt(e.target.value) || 10)} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">Default</Label>
                        <Input type="number" value={page.counter_default || 1} onChange={e => updatePage(index, 'counter_default', parseInt(e.target.value) || 1)} className="mt-1" />
                      </div>
                    </div>
                  )}

                  {/* Text input placeholder + helper text */}
                  {page.type === 'text_input' && (
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs">Placeholder Text</Label>
                        <Input value={page.placeholder || ''} onChange={e => updatePage(index, 'placeholder', e.target.value)} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">Helper Text <span className="text-gray-400">(optional — small text shown under the input)</span></Label>
                        <Input value={page.helper_text || ''} onChange={e => updatePage(index, 'helper_text', e.target.value)} className="mt-1" placeholder="e.g. We'll only use this to greet you in the app" data-testid={`page-helper-text-${index}`} />
                      </div>
                    </div>
                  )}

                  {/* Email capture / Password capture — placeholder + helper text */}
                  {(page.type === 'email_capture' || page.type === 'password_capture') && (
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs">Placeholder Text</Label>
                        <Input
                          value={page.placeholder || ''}
                          onChange={e => updatePage(index, 'placeholder', e.target.value)}
                          className="mt-1"
                          placeholder={page.type === 'email_capture' ? 'you@yourclub.com' : 'Password (min 6 characters)'}
                          data-testid={`page-placeholder-${index}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Helper Text <span className="text-gray-400">(small text shown under the input)</span></Label>
                        <Input
                          value={page.helper_text || ''}
                          onChange={e => updatePage(index, 'helper_text', e.target.value)}
                          className="mt-1"
                          placeholder={page.type === 'email_capture' ? "e.g. We'll never share your email" : 'e.g. At least 6 characters'}
                          data-testid={`page-helper-text-${index}`}
                        />
                      </div>
                      {page.type === 'email_capture' && (
                        <p className="text-[11px] text-gray-500 italic">
                          Also renders a "Continue with Google" button and a live sign-up counter (if today's signups ≥ 5 or week ≥ 3).
                        </p>
                      )}
                    </div>
                  )}

                  {/* Intro page — extra options (body text already shown above under Subtitle) */}
                  {page.type === 'intro' && (
                    <>
                      <div>
                        <Label className="text-xs">Time Estimate (e.g. "Estimated time: 2-3 minutes")</Label>
                        <Input value={page.time_estimate || ''} onChange={e => updatePage(index, 'time_estimate', e.target.value)} className="mt-1" />
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={!!page.show_skip_to_signup}
                            onChange={e => updatePage(index, 'show_skip_to_signup', e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300"
                            data-testid={`page-skip-toggle-${index}`}
                          />
                          <span className="text-xs font-medium text-gray-700">
                            Show "Skip to create account" link on this page
                          </span>
                        </label>
                        <p className="text-[11px] text-gray-500 leading-relaxed pl-6">
                          Lets coaches who already know they want to sign up jump straight to the email step (or plan page if email capture is disabled). Only shown to unregistered visitors.
                        </p>
                        {page.show_skip_to_signup && (
                          <div className="pl-6">
                            <Label className="text-xs">Link text</Label>
                            <Input
                              value={page.skip_to_signup_text || ''}
                              onChange={e => updatePage(index, 'skip_to_signup_text', e.target.value)}
                              className="mt-1"
                              placeholder="Skip ahead and create your account →"
                              data-testid={`page-skip-text-${index}`}
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Multi-select / Single-select options */}
                  {(page.type === 'multi_select' || page.type === 'single_select') && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs">Options</Label>
                        <Button variant="outline" size="sm" onClick={() => addOption(index)} className="h-7 text-xs gap-1">
                          <Plus className="w-3 h-3" /> Add Option
                        </Button>
                      </div>

                      {/* Single-select per-option redirect toggle */}
                      {page.type === 'single_select' && (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 mb-3 space-y-2">
                          <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={!!page.enable_option_redirects}
                              onChange={e => updatePage(index, 'enable_option_redirects', e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300"
                              data-testid={`page-redirect-toggle-${index}`}
                            />
                            <span className="text-xs font-medium text-gray-700">
                              Send users to a different page based on their answer
                            </span>
                          </label>
                          <p className="text-[11px] text-gray-500 leading-relaxed pl-6">
                            When enabled, each option below gets a destination dropdown. By default options just continue to the next page; you can route any option straight to "Create account" or "Plan & Payment".
                          </p>
                        </div>
                      )}

                      <div className={page.enable_option_redirects && page.type === 'single_select' ? 'space-y-2' : 'grid grid-cols-2 gap-2'}>
                        {(page.options || []).map((opt, optIdx) => {
                          const showRedirect = page.type === 'single_select' && !!page.enable_option_redirects;
                          const redirects = page.option_redirects || {};
                          const dest = redirects[opt] || 'next';
                          const setDest = (newDest) => {
                            const cleaned = { ...redirects };
                            if (newDest === 'next') {
                              delete cleaned[opt];
                            } else {
                              cleaned[opt] = newDest;
                            }
                            updatePage(index, 'option_redirects', cleaned);
                          };
                          return (
                            <div key={optIdx} className={showRedirect ? 'flex flex-col sm:flex-row sm:items-center gap-1.5' : 'flex items-center gap-1.5'}>
                              <div className="flex items-center gap-1.5 flex-1">
                                <Input
                                  value={opt}
                                  onChange={e => {
                                    // Keep redirect mapping in sync when option text changes
                                    if (showRedirect && redirects[opt] !== undefined) {
                                      const cleaned = { ...redirects };
                                      cleaned[e.target.value] = cleaned[opt];
                                      delete cleaned[opt];
                                      updatePage(index, 'option_redirects', cleaned);
                                    }
                                    updateOption(index, optIdx, e.target.value);
                                  }}
                                  className="h-8 text-sm"
                                  data-testid={`option-${index}-${optIdx}`}
                                />
                                <button
                                  onClick={() => moveOption(index, optIdx, -1)}
                                  disabled={optIdx === 0}
                                  className="text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                                  title="Move up"
                                  data-testid={`option-${index}-${optIdx}-up`}
                                >
                                  <ChevronUp className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => moveOption(index, optIdx, 1)}
                                  disabled={optIdx === (page.options || []).length - 1}
                                  className="text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                                  title="Move down"
                                  data-testid={`option-${index}-${optIdx}-down`}
                                >
                                  <ChevronDown className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => removeOption(index, optIdx)} className="text-red-400 hover:text-red-600 flex-shrink-0">
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                              {showRedirect && (
                                <select
                                  value={dest}
                                  onChange={e => setDest(e.target.value)}
                                  className="h-8 rounded-md border border-gray-200 px-2 text-xs sm:w-44 w-full"
                                  data-testid={`option-redirect-${index}-${optIdx}`}
                                >
                                  <option value="next">→ Next page</option>
                                  <option value="email_capture">→ Create account</option>
                                  <option value="plan_page">→ Plan &amp; Payment</option>
                                </select>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Features bullets */}
                  {page.type === 'features' && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs">Bullet Points (each shown with a green tick)</Label>
                        <Button variant="outline" size="sm" onClick={() => addBullet(index)} className="h-7 text-xs gap-1" data-testid={`add-bullet-${index}`}>
                          <Plus className="w-3 h-3" /> Add Bullet
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {(page.bullets || []).map((b, bIdx) => (
                          <div key={bIdx} className="flex items-center gap-1.5">
                            <Input
                              value={b}
                              onChange={e => updateBullet(index, bIdx, e.target.value)}
                              className="h-8 text-sm flex-1"
                              data-testid={`bullet-${index}-${bIdx}`}
                            />
                            <button
                              onClick={() => moveBullet(index, bIdx, -1)}
                              disabled={bIdx === 0}
                              className="text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                              title="Move up"
                              data-testid={`bullet-${index}-${bIdx}-up`}
                            >
                              <ChevronUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => moveBullet(index, bIdx, 1)}
                              disabled={bIdx === (page.bullets || []).length - 1}
                              className="text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                              title="Move down"
                              data-testid={`bullet-${index}-${bIdx}-down`}
                            >
                              <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => removeBullet(index, bIdx)} className="text-red-400 hover:text-red-600 flex-shrink-0">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                        {(page.bullets || []).length === 0 && (
                          <p className="text-xs text-gray-400 italic">No bullets yet — click "Add Bullet" to start.</p>
                        )}
                      </div>
                    </div>
                  )}

                          <div className="flex justify-end pt-2">
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => removePage(index)}
                              disabled={isCriticalPage(page)}
                              className="gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                              title={isCriticalPage(page) ? 'Required for sign-up — can\'t be removed' : undefined}
                              data-testid={`remove-page-${index}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" /> {isCriticalPage(page) ? 'Required page (locked)' : 'Remove Page'}
                            </Button>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  )}
                </SortablePageWrapper>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Plan Page Text */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Plan Selection Page</h3>
        <Card>
          <CardContent className="pt-4 space-y-4">
            <div>
              <Label className="text-xs">Greeting (use {'{name}'} for coach name)</Label>
              <Input value={config.plan_page?.greeting || ''} onChange={e => updatePlanPage('greeting', e.target.value)} className="mt-1" data-testid="plan-greeting" />
            </div>
            <div>
              <Label className="text-xs">Trial Offer Text (HTML supported)</Label>
              <textarea
                value={config.plan_page?.trial_text || ''}
                onChange={e => updatePlanPage('trial_text', e.target.value)}
                rows={3}
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                data-testid="plan-trial-text"
              />
            </div>
            <div>
              <Label className="text-xs">Experience Text (use {'{years}'} and {'{unit}'} placeholders)</Label>
              <textarea
                value={config.plan_page?.experience_text || ''}
                onChange={e => updatePlanPage('experience_text', e.target.value)}
                rows={3}
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                data-testid="plan-experience-text"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Features Title</Label>
                <Input value={config.plan_page?.features_title || ''} onChange={e => updatePlanPage('features_title', e.target.value)} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Features Description</Label>
              <textarea
                value={config.plan_page?.features_text || ''}
                onChange={e => updatePlanPage('features_text', e.target.value)}
                rows={3}
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Currency note <span className="text-gray-400">(small line under plan cards, e.g. &quot;Charged in EUR. See Terms.&quot;)</span></Label>
              <Input
                value={config.plan_page?.currency_note || ''}
                onChange={e => updatePlanPage('currency_note', e.target.value)}
                className="mt-1"
                placeholder="Charged in EUR. See Terms."
                data-testid="plan-currency-note-input"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Success Page Text */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Payment Success / Welcome Page</h3>
        <Card>
          <CardContent className="pt-4 space-y-4" data-testid="success-page-cms">
            <p className="text-xs text-gray-500 -mt-1">
              Edit every label on the celebration page shown after a coach completes payment.
              Use <code className="bg-gray-100 px-1 rounded">{'{name}'}</code> for coach first name and{' '}
              <code className="bg-gray-100 px-1 rounded">{'{team}'}</code> for team name.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Eyebrow (small caps above heading)</Label>
                <Input
                  value={config.success_page?.eyebrow || ''}
                  onChange={e => updateSuccessPage('eyebrow', e.target.value)}
                  className="mt-1"
                  data-testid="success-eyebrow"
                />
              </div>
              <div className="flex items-end gap-3 pb-1">
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="success-show-eyebrow-toggle">
                  <input
                    type="checkbox"
                    checked={config.success_page?.show_eyebrow !== false}
                    onChange={e => updateSuccessPage('show_eyebrow', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-xs text-gray-700">Show eyebrow on success page</span>
                </label>
              </div>
            </div>

            <div className="rounded-lg bg-emerald-50/60 border border-emerald-100 p-3 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="success-cheer-sound-toggle">
                <input
                  type="checkbox"
                  checked={config.success_page?.cheer_sound_enabled !== false}
                  onChange={e => updateSuccessPage('cheer_sound_enabled', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 mt-0.5"
                />
                <div>
                  <span className="text-sm font-semibold text-emerald-900">Enable crowd-cheer sound on goal moment</span>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Shows a small speaker icon next to the FT label after payment. Tap to hear a quick crowd cheer.
                    Sound never auto-plays — browsers require a user click. Toggle off to hide the speaker entirely.
                  </p>
                </div>
              </label>

              {config.success_page?.cheer_sound_enabled !== false && (
                <div className="pl-7 space-y-1.5">
                  <Label className="text-xs text-gray-700">Custom cheer audio URL (optional)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={config.success_page?.cheer_sound_url || ''}
                      onChange={e => updateSuccessPage('cheer_sound_url', e.target.value)}
                      className="flex-1 text-sm"
                      placeholder="https://your-cdn.com/cheer.mp3 (leave blank to use the default)"
                      data-testid="success-cheer-sound-url"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const src = (config.success_page?.cheer_sound_url || '').trim() || '/sounds/crowd-cheer.wav';
                        try {
                          const a = new Audio(src);
                          a.volume = 0.75;
                          a.play().catch(() => alert('Could not play that URL — check it returns an MP3/WAV file.'));
                        } catch {
                          alert('Could not play that URL.');
                        }
                      }}
                      className="px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
                      data-testid="success-cheer-sound-preview"
                    >
                      ▶ Preview
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-500">
                    Default ships with the app (≈155&nbsp;KB stadium roar). Drop in any public MP3/WAV URL to override it
                    — the Preview button plays whatever URL is currently saved (or the default if blank).
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Heading line 1</Label>
                <Input
                  value={config.success_page?.heading_line1 || ''}
                  onChange={e => updateSuccessPage('heading_line1', e.target.value)}
                  className="mt-1"
                  data-testid="success-heading-line1"
                />
              </div>
              <div>
                <Label className="text-xs">Heading line 2 (accent)</Label>
                <Input
                  value={config.success_page?.heading_line2 || ''}
                  onChange={e => updateSuccessPage('heading_line2', e.target.value)}
                  className="mt-1"
                  placeholder="Coach {name}!"
                  data-testid="success-heading-line2"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Subtitle (when team name is set)</Label>
              <textarea
                value={config.success_page?.subtitle_with_team || ''}
                onChange={e => updateSuccessPage('subtitle_with_team', e.target.value)}
                rows={2}
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                placeholder="You and {team} are all set up..."
                data-testid="success-subtitle-with-team"
              />
            </div>
            <div>
              <Label className="text-xs">Subtitle (when no team name)</Label>
              <textarea
                value={config.success_page?.subtitle_no_team || ''}
                onChange={e => updateSuccessPage('subtitle_no_team', e.target.value)}
                rows={2}
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                data-testid="success-subtitle-no-team"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">CTA Button Text</Label>
                <Input
                  value={config.success_page?.cta_button || ''}
                  onChange={e => updateSuccessPage('cta_button', e.target.value)}
                  className="mt-1"
                  data-testid="success-cta-button"
                />
              </div>
              <div>
                <Label className="text-xs">Email Hint (under CTA)</Label>
                <Input
                  value={config.success_page?.email_hint || ''}
                  onChange={e => updateSuccessPage('email_hint', e.target.value)}
                  className="mt-1"
                  data-testid="success-email-hint"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
              <div>
                <Label className="text-xs">Plan label · Monthly</Label>
                <Input
                  value={config.success_page?.plan_label_monthly || ''}
                  onChange={e => updateSuccessPage('plan_label_monthly', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Plan label · Annual</Label>
                <Input
                  value={config.success_page?.plan_label_annual || ''}
                  onChange={e => updateSuccessPage('plan_label_annual', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Plan label · Lifetime</Label>
                <Input
                  value={config.success_page?.plan_label_lifetime || ''}
                  onChange={e => updateSuccessPage('plan_label_lifetime', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Plan chip suffix (e.g. &quot;active&quot;)</Label>
              <Input
                value={config.success_page?.plan_chip_suffix || ''}
                onChange={e => updateSuccessPage('plan_chip_suffix', e.target.value)}
                className="mt-1"
                data-testid="success-plan-chip-suffix"
              />
            </div>

            <div className="border-t border-gray-200 pt-4 space-y-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Other states</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Processing heading</Label>
                  <Input
                    value={config.success_page?.processing_heading || ''}
                    onChange={e => updateSuccessPage('processing_heading', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Processing subtitle</Label>
                  <Input
                    value={config.success_page?.processing_subtitle || ''}
                    onChange={e => updateSuccessPage('processing_subtitle', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Error heading</Label>
                  <Input
                    value={config.success_page?.error_heading || ''}
                    onChange={e => updateSuccessPage('error_heading', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Error subtitle</Label>
                  <Input
                    value={config.success_page?.error_subtitle || ''}
                    onChange={e => updateSuccessPage('error_subtitle', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Error retry button</Label>
                  <Input
                    value={config.success_page?.error_retry_button || ''}
                    onChange={e => updateSuccessPage('error_retry_button', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Welcome / Confirmation Email */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Welcome Email (sent after payment)</h3>
        <Card>
          <CardContent className="pt-4 space-y-4" data-testid="welcome-email-cms">
            <p className="text-xs text-gray-500 -mt-1">
              Edit every label of the confirmation email coaches receive after a successful payment.
              Use <code className="bg-gray-100 px-1 rounded">{'{name}'}</code> for coach first name. HTML allowed in plan lines and bullets (e.g. <code className="bg-gray-100 px-1 rounded">&lt;strong&gt;</code>).
            </p>

            <div className="flex flex-wrap items-center gap-2 -mt-1">
              <span className="text-xs text-gray-500">Live preview:</span>
              <a
                href={`${API}/email-preview/welcome?name=Francis&plan=annual`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                data-testid="welcome-email-preview-annual"
              >Annual ↗</a>
              <a
                href={`${API}/email-preview/welcome?name=Francis&plan=monthly`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100"
              >Monthly ↗</a>
              <a
                href={`${API}/email-preview/welcome?name=Francis&plan=lifetime`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100"
              >Lifetime ↗</a>
              <span className="text-[10px] text-gray-400">(save first to see latest changes)</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Subject</Label>
                <Input
                  value={config.welcome_email?.subject || ''}
                  onChange={e => updateWelcomeEmail('subject', e.target.value)}
                  className="mt-1"
                  data-testid="welcome-subject"
                />
              </div>
              <div>
                <Label className="text-xs">Header banner title</Label>
                <Input
                  value={config.welcome_email?.header_title || ''}
                  onChange={e => updateWelcomeEmail('header_title', e.target.value)}
                  className="mt-1"
                  data-testid="welcome-header-title"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Goal Badge</Label>
                <Input
                  value={config.welcome_email?.goal_badge || ''}
                  onChange={e => updateWelcomeEmail('goal_badge', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Eyebrow (small caps)</Label>
                <Input
                  value={config.welcome_email?.eyebrow || ''}
                  onChange={e => updateWelcomeEmail('eyebrow', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Heading line 1</Label>
                <Input
                  value={config.welcome_email?.heading_line1 || ''}
                  onChange={e => updateWelcomeEmail('heading_line1', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Heading line 2 (accent)</Label>
                <Input
                  value={config.welcome_email?.heading_line2 || ''}
                  onChange={e => updateWelcomeEmail('heading_line2', e.target.value)}
                  className="mt-1"
                  placeholder="Coach {name}!"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Intro text (under heading)</Label>
              <textarea
                value={config.welcome_email?.intro_text || ''}
                onChange={e => updateWelcomeEmail('intro_text', e.target.value)}
                rows={2}
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
              />
            </div>

            <div className="border-t border-gray-200 pt-4 space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Plan summary box</p>
              <div>
                <Label className="text-xs">Plan box label (e.g. &quot;Your Plan&quot;)</Label>
                <Input
                  value={config.welcome_email?.plan_label || ''}
                  onChange={e => updateWelcomeEmail('plan_label', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Plan line · Monthly (HTML allowed)</Label>
                <textarea
                  value={config.welcome_email?.plan_line_monthly || ''}
                  onChange={e => updateWelcomeEmail('plan_line_monthly', e.target.value)}
                  rows={2}
                  className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Plan line · Annual (HTML allowed)</Label>
                <textarea
                  value={config.welcome_email?.plan_line_annual || ''}
                  onChange={e => updateWelcomeEmail('plan_line_annual', e.target.value)}
                  rows={3}
                  className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Plan line · Lifetime (HTML allowed)</Label>
                <textarea
                  value={config.welcome_email?.plan_line_lifetime || ''}
                  onChange={e => updateWelcomeEmail('plan_line_lifetime', e.target.value)}
                  rows={2}
                  className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">What's next bullets</p>
                <Button type="button" variant="outline" size="sm" onClick={addWelcomeBullet} data-testid="welcome-add-bullet">+ Add bullet</Button>
              </div>
              <div>
                <Label className="text-xs">Section title</Label>
                <Input
                  value={config.welcome_email?.whats_next_title || ''}
                  onChange={e => updateWelcomeEmail('whats_next_title', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="space-y-2">
                {(config.welcome_email?.whats_next_bullets || []).map((bullet, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="flex flex-col">
                      <button
                        type="button"
                        onClick={() => moveWelcomeBullet(i, -1)}
                        disabled={i === 0}
                        className="text-xs px-1.5 py-0.5 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                      >▲</button>
                      <button
                        type="button"
                        onClick={() => moveWelcomeBullet(i, 1)}
                        disabled={i === (config.welcome_email?.whats_next_bullets?.length || 0) - 1}
                        className="text-xs px-1.5 py-0.5 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed mt-0.5"
                      >▼</button>
                    </div>
                    <Input
                      value={bullet}
                      onChange={e => updateWelcomeBullet(i, e.target.value)}
                      className="flex-1"
                    />
                    <Button type="button" variant="outline" size="sm" onClick={() => removeWelcomeBullet(i)} className="text-red-500 hover:text-red-600">×</Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4 space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Call to action</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">CTA button text</Label>
                  <Input
                    value={config.welcome_email?.cta_text || ''}
                    onChange={e => updateWelcomeEmail('cta_text', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">CTA destination path (e.g. /clubhouse)</Label>
                  <Input
                    value={config.welcome_email?.cta_path || ''}
                    onChange={e => updateWelcomeEmail('cta_path', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs">Sign-off (final paragraph)</Label>
              <textarea
                value={config.welcome_email?.signoff || ''}
                onChange={e => updateWelcomeEmail('signoff', e.target.value)}
                rows={2}
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Step Labels */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Step Labels</h3>
        <Card>
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {(config.step_labels || ['Get to know you', 'Setup', 'Choose Plan']).map((label, i) => (
                <div key={i}>
                  <Label className="text-xs">Step {i + 1}</Label>
                  <Input
                    value={label}
                    onChange={e => {
                      const labels = [...(config.step_labels || ['Get to know you', 'Setup', 'Choose Plan'])];
                      labels[i] = e.target.value;
                      setConfig(prev => ({ ...prev, step_labels: labels }));
                    }}
                    className="mt-1"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={saveConfig} disabled={saving} className="gap-2" data-testid="save-onboarding-btn">
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Onboarding Config'}
        </Button>
      </div>
    </div>
  );
}
